import { Avatar } from '@/components/ui';
import { InfoTip, type InfoGroup } from '@/components/InfoTip';
import { formatBRL, formatBRLShort, formatNumber, formatPercent } from '@/lib/format';
import type { RankingEntry, RankingMetric } from '@/lib/types';
import { formatMetric, receiptsLabel, salesLabel } from './rankingFormat';
import './ranking.css';

/** O pódio desenha o 2º à esquerda e o 3º à direita do 1º, como num pódio de verdade. */
const PODIUM_ORDER = [1, 0, 2] as const;

/** Uma linha do pódio e da classificação — de um membro ou de um cliente. */
export interface BoardItem {
  id: string;
  position: number;
  name: string;
  avatarColor: string;
  photoUrl: string | null;
  /** É por ele que a lista ordena; a barra mede a distância até o líder. */
  score: number;
  /** O valor do jeito que se lê. */
  scoreLabel: string;
  /** O que explica o valor (comprovantes, checkout, fatia): fica no "i", ao lado do valor. */
  details: string[];
  /** O que ficou fora do valor (na venda): vem num bloco à parte do "i". */
  outside?: string[];
  growth: number | null;
  /** O valor do período anterior, já formatado — aparece ao passar o mouse. */
  previousLabel: string;
  positionChange: number | null;
  inactive?: boolean;
}

/** Abrir uma linha leva a outra visão (ex.: os clientes do membro). O nome vira o botão, e o clique vale na linha inteira. */
export interface BoardSelect {
  label: (item: BoardItem) => string;
  onSelect: (item: BoardItem) => void;
}

interface BoardProps {
  items: BoardItem[];
  select?: BoardSelect;
  /** O período da comparação por extenso ("setembro de 2026"); null quando não há (período aberto). */
  previousPeriod?: string | null;
  /** O "i" de cada linha; o ranking de membros o mostra só ao administrador. */
  withInfo?: boolean;
}

/** Quem pontuou vai ao pódio (até três); o resto da classificação vem logo abaixo, inteiro. */
export function splitPodium<T extends { score: number }>(entries: T[]): { podium: T[]; rest: T[] } {
  const podium = entries.slice(0, 3).filter((entry) => entry.score > 0);
  return { podium, rest: entries.slice(podium.length) };
}

/**
 * O detalhe de um membro, no "i" ao lado do valor. Na venda, o valor é o que
 * entrou pelo link do membro e o que o administrador comprovou: de quantas
 * vendas veio e a fatia do time.
 * Nas outras métricas: os comprovantes, quanto veio do checkout e a fatia do vendido.
 */
function detailsOf(entry: RankingEntry, metric: RankingMetric): string[] {
  if (entry.receiptCount === 0) return ['Ainda não pontuou no período'];
  if (metric === 'sold') {
    const link = entry.linkCount > 0 ? `${salesLabel(entry.linkCount)} pelo link` : 'Nenhuma venda pelo link no período';
    const share = `${formatPercent(entry.checkoutShare, 1)} do time`;
    if (entry.adminConfirmedCount === 0) return [entry.linkCount > 0 ? `${link} · ${share}` : link];
    return [link, `Comprovadas pelo admin: ${formatBRL(entry.adminConfirmedTotal)} (${formatNumber(entry.adminConfirmedCount)})`, share];
  }
  const parts = [receiptsLabel(entry.receiptCount)];
  if (entry.checkoutCount > 0) parts.push(`${formatBRLShort(entry.checkoutTotal)} pelo checkout`);
  // Quem só tem repasse de lives não vendeu: a fatia do vendido não diz nada.
  if (entry.soldTotal > 0) parts.push(`${formatPercent(entry.share, 1)} do vendido`);
  return parts;
}

/**
 * Na venda, o que ficou fora do valor, num bloco à parte do "i": os pedidos
 * sem o código do membro (o dono veio do comprovante casado ou do
 * administrador), os comprovantes de venda anexados que ainda contam (só nos
 * meses que os tiveram; não há mais novos) e o total com eles.
 */
function outsideOf(entry: RankingEntry, metric: RankingMetric): string[] {
  if (metric !== 'sold') return [];
  const valued = (total: number, count: number) => `${formatBRL(total)} (${formatNumber(count)})`;
  const lines = [
    entry.ownedByReceiptCount > 0 ? `Pedidos sem código, dono pelo comprovante: ${valued(entry.ownedByReceiptTotal, entry.ownedByReceiptCount)}` : null,
    entry.ownedByAdminCount > 0 ? `Pedidos sem código, dono dado pelo admin: ${valued(entry.ownedByAdminTotal, entry.ownedByAdminCount)}` : null,
    entry.attachedSoldCount > 0 ? `Comprovantes de venda: ${valued(entry.attachedSoldTotal, entry.attachedSoldCount)}` : null,
  ].filter((line): line is string => line !== null);
  return lines.length > 0 ? [...lines, `Total com eles: ${formatBRL(entry.soldTotal)}`] : [];
}

/**
 * A comparação com o período anterior, no último bloco do "i": qual período
 * (com os mesmos filtros), o valor de lá com a variação, e a posição de agora e a de lá.
 */
function comparisonOf(item: BoardItem, previousPeriod: string | null | undefined): InfoGroup {
  if (previousPeriod === null) return { lines: ['Sem período anterior para comparar'] };
  const value = item.growth === null
    ? `${item.previousLabel} · sem base de comparação`
    : `${item.previousLabel} · ${item.growth >= 0 ? 'alta' : 'queda'} de ${formatPercent(Math.abs(item.growth))}`;
  return { title: `Período anterior${previousPeriod ? ` (${previousPeriod})` : ''}, mesmos filtros`, lines: [value, placeOf(item)] };
}

/** O "i" em blocos: o que explica o valor, o que ficou fora dele e a comparação com o período anterior. */
const infoOf = (item: BoardItem, previousPeriod: string | null | undefined): InfoGroup[] => [
  { lines: item.details },
  ...(item.outside?.length ? [{ title: 'Fora do ranking', lines: item.outside }] : []),
  comparisonOf(item, previousPeriod),
];

/** A posição de agora e a do período anterior, por extenso. */
function placeOf(item: BoardItem): string {
  if (item.score <= 0) return 'Sem posição no período';
  const change = item.positionChange;
  if (change === null) return `${item.position}º lugar · não pontuou no período anterior`;
  if (change === 0) return `${item.position}º lugar · o mesmo do período anterior`;
  const moved = Math.abs(change);
  return `${item.position}º lugar · era ${item.position + change}º (${change > 0 ? 'subiu' : 'caiu'} ${moved} ${moved === 1 ? 'posição' : 'posições'})`;
}

/** Um membro do ranking de membros, pela métrica escolhida. */
export const memberItem = (entry: RankingEntry, metric: RankingMetric): BoardItem => ({
  id: entry.member.id,
  position: entry.position,
  name: entry.member.name,
  avatarColor: entry.member.avatarColor,
  photoUrl: entry.member.photoUrl,
  score: entry.score,
  scoreLabel: formatMetric(metric, entry.score),
  details: detailsOf(entry, metric),
  outside: outsideOf(entry, metric),
  growth: entry.growth,
  previousLabel: formatMetric(metric, entry.previousScore),
  positionChange: entry.positionChange,
  inactive: !entry.member.active,
});

function Name({ item, select }: { item: BoardItem; select?: BoardSelect }) {
  if (!select) return <span className="truncate" title={item.name}>{item.name}</span>;
  return (
    <button type="button" className="link-btn board-select truncate" title={select.label(item)} aria-label={select.label(item)} onClick={() => select.onSelect(item)}>
      {item.name}
    </button>
  );
}

export function Podium({ items, select, previousPeriod, withInfo = true }: BoardProps) {
  const slots = PODIUM_ORDER.map((index) => items[index]).filter((item): item is BoardItem => Boolean(item));
  return (
    <ol className="podium" aria-label="Pódio" data-count={items.length}>
      {slots.map((item) => (
        <li key={item.id} className="podium-slot" data-place={item.position}>
          <div className="podium-card" data-selectable={select ? true : undefined}>
            <div className="podium-avatar">
              <Avatar name={item.name} color={item.avatarColor} photo={item.photoUrl} size={item.position === 1 ? 64 : 52} />
            </div>
            <div className="podium-name truncate"><Name item={item} select={select} /></div>
            {/* Só o valor à vista; o que o explica e a comparação ficam no "i". */}
            <div className="podium-value">
              <span className="display num podium-score">{item.scoreLabel}</span>
              {withInfo ? <InfoTip label={`Detalhes de ${item.name}`} groups={infoOf(item, previousPeriod)} /> : null}
            </div>
          </div>
          <div className="podium-step" aria-hidden="true">
            <span className="display num">{item.position}</span>
          </div>
          <span className="sr-only">{item.position}º lugar</span>
        </li>
      ))}
    </ol>
  );
}

/** A classificação de todos, com a distância até o líder em barra. */
export function LeaderboardList({ items, leaderScore, select, previousPeriod, withInfo = true }: BoardProps & { leaderScore: number }) {
  return (
    <ol className="board" aria-label="Classificação">
      {items.map((item) => {
        const scored = item.score > 0;
        const ratio = leaderScore > 0 ? Math.min(item.score / leaderScore, 1) : 0;
        return (
          <li
            key={item.id}
            className="board-row"
            data-scored={scored}
            data-place={scored ? item.position : undefined}
            data-selectable={select ? true : undefined}
          >
            {/* Quem não pontuou empata em zero: um traço, em vez de todos no mesmo lugar. */}
            <span className="display num board-pos">{scored ? item.position : '–'}</span>
            <Avatar name={item.name} color={item.avatarColor} photo={item.photoUrl} size={32} />
            <div className="board-who">
              <span className="board-name">
                <Name item={item} select={select} />
                {item.inactive ? <span className="chip">inativo</span> : null}
              </span>
            </div>
            <div className="board-bar bar-track" aria-hidden="true">
              <div className="bar-fill" style={{ width: `${ratio * 100}%` }} />
            </div>
            <span className="board-score">
              <span className="num">{item.scoreLabel}</span>
              {withInfo ? <InfoTip label={`Detalhes de ${item.name}`} groups={infoOf(item, previousPeriod)} /> : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
